import { all, call, takeLeading, put } from '@redux-saga/core/effects';
import axios from 'axios';

import { ERROR, SUCCESS } from '../../utils/constants';
import { getErrorMessage } from '../../utils/api-util';
import { filterListings, retrieveFromLocalStorage } from '../../utils/util';

import {
  requestViewAllClassifications,
  requestViewAllCategories,
  requestViewAllTypes,
  requestViewMetricUnits,
  requestViewAllMetricUnits,
  requestCreateProductListing,
  requestViewPersonProductListings,
  requestViewProductListing,
  requestUpdateProductListing,
  requestCreateBuyerListing,
  requestViewPersonBuyerListings,
  requestViewBuyerListing,
  requestUpdateBuyerListing,
  requestCreateSpecification,
  requestViewAllSpecifications,
  requestUpdateClassification,
  requestUpdateCategory,
  requestUpdateType,
  requestViewAllTypesRegistration,
  requestBookmarkProductListing,
  requestViewMarketBuyerListings,
  requestViewMarketProductListings,
  requestViewDashboard,
  requestCopyListing,
} from './product.actions';
import {
  getViewAllClassificationsRequest,
  getViewAllCategoriesRequest,
  getViewAllTypesRequest,
  getViewMetricUnitsRequest,
  getViewAllMetricUnitsRequest,
  getCreateProductListingRequest,
  getViewPersonProductListingsRequest,
  getViewProductListingRequest,
  getUpdateProductListingRequest,
  getCreateBuyerListingRequest,
  getViewPersonBuyerListingsRequest,
  getViewBuyerListingRequest,
  getUpdateBuyerListingRequest,
  getCreateSpecificationRequest,
  getViewAllSpecificationsRequest,
  getUpdateClassificationRequest,
  getUpdateCategoryRequest,
  getUpdateTypeRequest,
  getViewMarketBuyerListingsRequest,
  getViewMarketProductListingsRequest,
  getBookmarkProductListingRequest,
  getViewAllTypesRegistrationRequest,
  getViewDashboardRequest,
  getCopyListingRequest,
} from './product.endpoints';

import { addSystemNotice, addSpinner, removeSpinner } from '../../slices/system/system.slice';
import {
  setClassifications,
  setCategories,
  setTypes,
  setMetricUnits,
  setProductListings,
  setProductListing,
  setBuyerListings,
  setBuyerListing,
  setSpecifications,
  setTypesRegistration,
} from '../../slices/product/product.slice';
import {
  setFeaturedListings as setDashboardFeaturedListings,
  setBookmarkedListings as setDashboardBookmarkedListings,
  setProductListings as setDashboardProductListings,
} from '../../slices/dashboard/dashboard.slice';

export function* performViewAllClassificationsRequest() {
  try {
    yield put(addSpinner('Retrieving Classifications'));

    const [endpoint, options] = getViewAllClassificationsRequest();

    const { data } = yield call(axios, endpoint, options);

    yield put(setClassifications(data.classifications));
  } catch (error) {
    yield put(setClassifications([]));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Classifications'));
  }
}

export function* watchForViewAllClassificationsRequest() {
  yield takeLeading(requestViewAllClassifications.type, performViewAllClassificationsRequest);
}

export function* performViewAllCategoriesRequest({ payload }) {
  const { classificationExternalReference } = payload;

  try {
    yield put(addSpinner('Retrieving Categories'));

    const [endpoint, options] = getViewAllCategoriesRequest(classificationExternalReference);

    const { data } = yield call(axios, endpoint, options);

    yield put(setCategories(data.categories));
  } catch (error) {
    yield put(setCategories([]));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Categories'));
  }
}

export function* watchForViewAllCategoriesRequest() {
  yield takeLeading(requestViewAllCategories.type, performViewAllCategoriesRequest);
}

export function* performViewAllTypesRequest({ payload }) {
  const { categoryExternalReference } = payload;

  try {
    yield put(addSpinner('Retrieving Types'));

    const [endpoint, options] = getViewAllTypesRequest(categoryExternalReference);

    const { data } = yield call(axios, endpoint, options);

    yield put(setTypes(data.types));
  } catch (error) {
    yield put(setTypes([]));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Types'));
  }
}

export function* watchForViewAllTypesRequest() {
  yield takeLeading(requestViewAllTypes.type, performViewAllTypesRequest);
}

export function* performViewMetricUnitsRequest({ payload }) {
  const { typeExternalReference } = payload;

  try {
    yield put(addSpinner('Retrieving Metric Units'));

    const [endpoint, options] = getViewMetricUnitsRequest(typeExternalReference);

    const { data } = yield call(axios, endpoint, options);

    const metricUnits = data.metricUnits.map((metricUnit) => ({
      label: metricUnit,
      value: metricUnit,
    }));

    yield put(setMetricUnits(metricUnits));
  } catch (error) {
    yield put(setMetricUnits([]));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Metric Units'));
  }
}

export function* watchForViewMetricUnitsRequest() {
  yield takeLeading(requestViewMetricUnits.type, performViewMetricUnitsRequest);
}

export function* performViewAllMetricUnitsRequest() {
  try {
    yield put(addSpinner('Retrieving Metric Units'));

    const [endpoint, options] = getViewAllMetricUnitsRequest();

    const { data } = yield call(axios, endpoint, options);

    const metricUnits = data.metricUnits.map((metricUnit) => ({
      label: metricUnit,
      value: metricUnit,
    }));

    yield put(setMetricUnits(metricUnits));
  } catch (error) {
    yield put(setMetricUnits([]));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Metric Units'));
  }
}

export function* watchForViewAllMetricUnitsRequest() {
  yield takeLeading(requestViewAllMetricUnits.type, performViewAllMetricUnitsRequest);
}

export function* performCreateProductListingRequest({ payload }) {
  const { productListing, onSuccess } = payload;

  try {
    yield put(addSpinner('Creating Product Listing'));

    const [endpoint, options] = getCreateProductListingRequest(productListing);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Creating Product Listing'));
  }
}

export function* watchForCreateProductListingRequest() {
  yield takeLeading(requestCreateProductListing.type, performCreateProductListingRequest);
}

export function* performViewAllTypesRegistrationRequest() {
  try {
    yield put(addSpinner('Retrieving All Types'));

    const [endpoint, options] = getViewAllTypesRegistrationRequest();

    const { data } = yield call(axios, endpoint, options);

    yield put(setTypesRegistration(data));
  } catch (error) {
    yield put(setTypes([]));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving All Types'));
  }
}

export function* watchForViewAllTypesRegistrationRequest() {
  yield takeLeading(requestViewAllTypesRegistration.type, performViewAllTypesRegistrationRequest);
}

export function* performBookmarkProductListingRequest({ payload }) {
  const { productExternalReference, type, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating Bookmark'));

    const [endpoint, options] = getBookmarkProductListingRequest(productExternalReference, type);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Updating Bookmark'));
  }
}

export function* watchForBookmarkProductListingRequest() {
  yield takeLeading(requestBookmarkProductListing.type, performBookmarkProductListingRequest);
}

export function* performViewPersonProductListingsRequest({ payload }) {
  const { filters } = payload;

  try {
    yield put(addSpinner('Retrieving Product Listings'));

    const [endpoint, options] = getViewPersonProductListingsRequest(filters);

    const { data } = yield call(axios, endpoint, options);
    const { sellerListings } = data;

    const filteredListings = filterListings(sellerListings, filters.searchString);

    yield put(setProductListings(filteredListings));
  } catch (error) {
    yield put(setProductListings([]));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Product Listings'));
  }
}

export function* watchForViewPersonProductListingsRequest() {
  yield takeLeading(requestViewPersonProductListings.type, performViewPersonProductListingsRequest);
}

export function* performViewMarketProductListingsRequest({ payload }) {
  const { filters } = payload;

  try {
    yield put(addSpinner('Retrieving Product Listings'));

    const [endpoint, options] = getViewMarketProductListingsRequest(filters);

    const { data } = yield call(axios, endpoint, options);
    const { sellerListings } = data;

    const filteredListings = filterListings(sellerListings, filters.searchString);

    yield put(setProductListings(filteredListings));
  } catch (error) {
    yield put(setProductListings([]));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Product Listings'));
  }
}

export function* watchForViewMarketProductListingsRequest() {
  yield takeLeading(requestViewMarketProductListings.type, performViewMarketProductListingsRequest);
}

export function* performViewMarketBuyerListingsRequest({ payload }) {
  const { filters } = payload;

  try {
    yield put(addSpinner('Retrieving Buyer Listings'));

    const [endpoint, options] = getViewMarketBuyerListingsRequest(filters);

    const { data } = yield call(axios, endpoint, options);
    const { buyerListings } = data;

    const filteredListings = filterListings(buyerListings, filters.searchString);

    yield put(setBuyerListings(filteredListings));
  } catch (error) {
    yield put(setBuyerListings([]));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Buyer Listings'));
  }
}

export function* watchForViewMarketBuyerListingsRequest() {
  yield takeLeading(requestViewMarketBuyerListings.type, performViewMarketBuyerListingsRequest);
}

export function* performViewProductListingRequest({ payload }) {
  const { productExternalReference } = payload;

  try {
    yield put(addSpinner('Retrieving Product Listing'));

    const [endpoint, options] = getViewProductListingRequest(productExternalReference);

    const { data } = yield call(axios, endpoint, options);

    yield put(setProductListing(data.sellerListingVO));
  } catch (error) {
    yield put(setProductListing(undefined));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Product Listing'));
  }
}

export function* watchForViewProductListingRequest() {
  yield takeLeading(requestViewProductListing.type, performViewProductListingRequest);
}

export function* performUpdateProductListingRequest({ payload }) {
  const { productListing, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating Product Listing'));

    const [endpoint, options] = getUpdateProductListingRequest(productListing);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Updating Product Listing'));
  }
}

export function* watchForUpdateProductListingRequest() {
  yield takeLeading(requestUpdateProductListing.type, performUpdateProductListingRequest);
}

export function* performCreateBuyerListingRequest({ payload }) {
  const { buyerListing, onSuccess } = payload;

  try {
    yield put(addSpinner('Creating Buyer Listing'));

    const [endpoint, options] = getCreateBuyerListingRequest(buyerListing);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Creating Buyer Listing'));
  }
}

export function* watchForCreateBuyerListingRequest() {
  yield takeLeading(requestCreateBuyerListing.type, performCreateBuyerListingRequest);
}

export function* performViewPersonBuyerListingsRequest({ payload }) {
  const { filters } = payload;

  try {
    yield put(addSpinner('Retrieving Buyer Listings'));

    const [endpoint, options] = getViewPersonBuyerListingsRequest(filters);

    const { data } = yield call(axios, endpoint, options);
    const { buyerListings } = data;

    const filteredListings = filterListings(buyerListings, filters.searchString);

    yield put(setBuyerListings(filteredListings));
  } catch (error) {
    yield put(setBuyerListings([]));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Buyer Listings'));
  }
}

export function* watchForViewPersonBuyerListingsRequest() {
  yield takeLeading(requestViewPersonBuyerListings.type, performViewPersonBuyerListingsRequest);
}

export function* performViewBuyerListingRequest({ payload }) {
  const { productExternalReference } = payload;

  try {
    yield put(addSpinner('Retrieving Buyer Listing'));

    const [endpoint, options] = getViewBuyerListingRequest(productExternalReference);

    const { data } = yield call(axios, endpoint, options);

    yield put(setBuyerListing(data.buyerListing));
  } catch (error) {
    yield put(setBuyerListing(undefined));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Buyer Listing'));
  }
}

export function* watchForViewBuyerListingRequest() {
  yield takeLeading(requestViewBuyerListing.type, performViewBuyerListingRequest);
}

export function* performUpdateBuyerListingRequest({ payload }) {
  const { buyerListing, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating Buyer Listing'));

    const [endpoint, options] = getUpdateBuyerListingRequest(buyerListing);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Updating Buyer Listing'));
  }
}

export function* watchForUpdateBuyerListingRequest() {
  yield takeLeading(requestUpdateBuyerListing.type, performUpdateBuyerListingRequest);
}

export function* performCreateSpecificationRequest({ payload }) {
  const { specification, onSuccess } = payload;

  try {
    yield put(addSpinner('Creating Product Specification'));

    const [endpoint, options] = getCreateSpecificationRequest(specification);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Creating Product Specification'));
  }
}

export function* watchForCreateSpecificationRequest() {
  yield takeLeading(requestCreateSpecification.type, performCreateSpecificationRequest);
}

export function* performViewAllSpecificationsRequest({ payload }) {
  const { filters, onSuccess } = payload;

  try {
    yield put(addSpinner('Retrieving Product Specifications'));

    const [endpoint, options] = getViewAllSpecificationsRequest(filters);

    const { data } = yield call(axios, endpoint, options);
    const { specifications, totalSpecifications } = data;

    if (onSuccess) {
      yield call(onSuccess, totalSpecifications);
    }

    yield put(setSpecifications(specifications));
  } catch (error) {
    yield put(setSpecifications([]));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Product Specifications'));
  }
}

export function* watchForViewAllSpecificationsRequest() {
  yield takeLeading(requestViewAllSpecifications.type, performViewAllSpecificationsRequest);
}

export function* performUpdateClassificationRequest({ payload }) {
  const { classification, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating Product Classification'));

    const [endpoint, options] = getUpdateClassificationRequest(classification);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Updating Product Classification'));
  }
}

export function* watchForUpdateClassificationRequest() {
  yield takeLeading(requestUpdateClassification.type, performUpdateClassificationRequest);
}

export function* performUpdateCategoryRequest({ payload }) {
  const { category, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating Product Category'));

    const [endpoint, options] = getUpdateCategoryRequest(category);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Updating Product Category'));
  }
}

export function* watchForUpdateCategoryRequest() {
  yield takeLeading(requestUpdateCategory.type, performUpdateCategoryRequest);
}

export function* performUpdateTypeRequest({ payload }) {
  const { type, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating Product Type'));

    const [endpoint, options] = getUpdateTypeRequest(type);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Updating Product Type'));
  }
}

export function* watchForUpdateTypeRequest() {
  yield takeLeading(requestUpdateType.type, performUpdateTypeRequest);
}

export function* performViewDashboard() {
  try {
    yield put(addSpinner('Retrieving Dashboard'));

    const personExternalReference = retrieveFromLocalStorage('personExternalReference');

    const [endpoint, options] = getViewDashboardRequest(personExternalReference);

    const { data } = yield call(axios, endpoint, options);
    const { bookMarkedBuyerProducts, bookMarkedSellerProducts, featuredProducts, productListings } = data;

    const mappedBookMarkedBuyerProducts = bookMarkedBuyerProducts.map((bookMarkedBuyerProduct) => ({
      ...bookMarkedBuyerProduct,
      isBuyer: true,
    }));

    const mappedBookMarkedSellerProducts = bookMarkedSellerProducts.map((bookMarkedSellerProduct) => ({
      ...bookMarkedSellerProduct,
      isBuyer: false,
    }));

    yield put(setDashboardBookmarkedListings([...mappedBookMarkedSellerProducts, ...mappedBookMarkedBuyerProducts]));
    yield put(setDashboardFeaturedListings(featuredProducts));
    yield put(setDashboardProductListings(productListings));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Dashboard'));
  }
}

export function* watchForViewDashboard() {
  yield takeLeading(requestViewDashboard.type, performViewDashboard);
}

export function* performCopyListingRequest({ payload }) {
  const { copyListingRequest, onSuccess } = payload;

  try {
    yield put(addSpinner('Copying Product Listing'));

    const [endpoint, options] = getCopyListingRequest(copyListingRequest);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Copying Listing'));
  }
}

export function* watchForCopyListingRequest() {
  yield takeLeading(requestCopyListing.type, performCopyListingRequest);
}

export default function* productSaga() {
  yield all([
    watchForViewAllClassificationsRequest(),
    watchForViewAllCategoriesRequest(),
    watchForViewAllTypesRequest(),
    watchForViewMetricUnitsRequest(),
    watchForViewAllMetricUnitsRequest(),
    watchForCreateProductListingRequest(),
    watchForViewPersonProductListingsRequest(),
    watchForViewProductListingRequest(),
    watchForUpdateProductListingRequest(),
    watchForCreateBuyerListingRequest(),
    watchForViewPersonBuyerListingsRequest(),
    watchForViewBuyerListingRequest(),
    watchForUpdateBuyerListingRequest(),
    watchForCreateSpecificationRequest(),
    watchForViewAllSpecificationsRequest(),
    watchForUpdateClassificationRequest(),
    watchForUpdateCategoryRequest(),
    watchForUpdateTypeRequest(),
    watchForViewAllTypesRegistrationRequest(),
    watchForBookmarkProductListingRequest(),
    watchForViewMarketBuyerListingsRequest(),
    watchForViewMarketProductListingsRequest(),
    watchForViewDashboard(),
    watchForCopyListingRequest(),
  ]);
}
